<script setup lang="ts">
import Email from "@/components/common/Integration/Email.vue";
import { useAppStore } from "@/store/app";
import { UITag } from "@gohighlevel/ghl-ui";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import BrowserFrame from "./BrowserFrame.vue";

const store = useAppStore();
const { t } = useI18n();
const emailMetaRows = computed(() => [
  { key: "to", label: t("integrate.email.to") },
  { key: "from", label: t("integrate.email.from") },
]);
</script>

<template>
  <section class="email-pane">
    <div
      class="email-pane__preview-shell email-pane__preview-shell--scrollable integrate-preview-card"
    >
      <div class="email-pane__preview-label integrate-preview-chip">
        <UITag
          id="email-preview-tag"
          type="info"
          size="small"
          :bordered="false"
        >
          {{ t("integrate.email.preview") }}
        </UITag>
      </div>
      <BrowserFrame>
        <article data-testid="email-preview" class="email-preview">
          <div class="email-preview__meta">
            <div
              v-for="row in emailMetaRows"
              :key="row.key"
              data-testid="email-preview-meta-row"
              class="email-preview__meta-row"
            >
              <span
                data-testid="email-preview-meta-label"
                class="email-preview__meta-label"
              >
                {{ row.label }}
              </span>
              <span class="email-preview__meta-pill" aria-hidden="true"></span>
            </div>
          </div>
          <div class="email-preview__divider" aria-hidden="true"></div>
          <div class="email-preview__body">
            <p>{{ t("integrate.email.inviteTitle") }}</p>
            <p>{{ t("integrate.email.inviteBody") }}</p>
            <span
              class="email-preview__button email-preview__button--auto"
              aria-hidden="true"
            >
              {{ t("integrate.email.inviteAction") }}
            </span>
          </div>
        </article>
      </BrowserFrame>
    </div>

    <aside class="email-pane__actions email-pane__actions--scrollable">
      <Email :widget-id="store.formId" variant="panel" />
    </aside>
  </section>
</template>

<style scoped>
.email-pane {
  display: grid;
  grid-template-columns: minmax(0, 1fr) var(--integrate-side-panel-width);
  width: 100%;
  min-width: 0;
  min-height: 0;
  height: 100%;
  overflow: hidden;
  gap: 12px;
}

.email-pane__actions {
  min-width: 0;
  border-radius: var(--cp-radius-lg, 12px);
  background: var(--base-white, var(--cp-surface-50));
}

.email-pane__preview-shell--scrollable,
.email-pane__actions--scrollable {
  min-height: 0;
  overflow-y: auto;
}

.email-pane__preview-label {
  width: 100%;
  align-self: flex-start;
}

.email-pane :deep(.browser-frame) {
  max-width: none;
  min-height: 0;
  flex: 1;
}
.email-pane :deep(.browser-frame--mobile) {
  width: min(100%, 390px);
  max-width: 390px;
}
.email-pane :deep(.browser-frame__content) {
  min-height: 0;
  height: calc(100% - 40px);
  padding: 0;
  background: var(--base-white, #fff);
}

.email-preview {
  width: 100%;
  min-height: 440px;
  height: 100%;
  box-sizing: border-box;
  background: var(--base-white, var(--cp-surface-50));
}

.email-preview__meta {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 40px;
}
.email-preview__meta-row {
  display: grid;
  grid-template-columns: 36px minmax(0, 1fr);
  align-items: center;
  gap: 12px;
}
.email-preview__meta-label {
  color: var(--gray-500, #667085);
  font-size: 12px;
  line-height: 18px;
}
.email-preview__meta-pill {
  width: 100%;
  height: 12px;
  border-radius: 999px;
  background: var(--gray-100, #f2f4f7);
}
.email-preview__divider {
  width: 100%;
  height: 1px;
  background: var(--gray-200, #eaecf0);
}
.email-preview__body {
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  padding: 32px 40px 40px;
}
.email-preview__body p {
  margin: 0;
  color: var(--gray-900, #101828);
  font-size: 14px;
  line-height: 20px;
}
.email-preview__body p:first-child {
  margin-bottom: 12px;
}
.email-preview__body p:nth-child(2) {
  margin-bottom: 16px;
}
.email-preview__button {
  display: inline-flex;
  min-height: 36px;
  align-items: center;
  padding: 0 14px;
  border-radius: 8px;
  color: var(--base-white, #fff);
  background: var(--primary-600, var(--cp-primary-600));
  font-size: 14px;
  font-weight: 500;
}
.email-preview__button--auto {
  width: auto;
}

.email-pane__actions {
  overflow: auto;
  padding: 20px;
}

.email-pane__actions :deep(> section) {
  width: 100%;
  min-width: 0;
}

@media (max-width: 1024px) {
  .email-pane {
    grid-template-columns: minmax(0, 1fr);
  }
  .email-pane__actions {
    min-height: 420px;
  }
}
</style>
